/*
 * <product-carousel>
 *
 * Fills the shared carousel track used by the "Recently viewed" and
 * "Suggestions" sections. One component, two sources:
 *
 *   data-source="recently-viewed"  reads the shopify_recently_viewed cookie and
 *                                  fetches a catalogue card per handle
 *   data-source="recommendations"  asks Shopify's Product Recommendations API
 *
 * There is no slider here on purpose. The layout is CSS scroll-snap
 * (assets/section-product-carousel.css), so it does not matter when the cards
 * arrive — which is the whole reason this component can stay this small.
 *
 * Both sources hide the section outright when they come back with nothing, so a
 * heading is never left stranded above an empty row.
 *
 * Cookie ownership: theme.js keeps WRITING the recently-viewed cookie
 * (Shopify.Products.recordRecentlyViewed). This component only READS it. Do not
 * reintroduce writing here or the two will fight.
 */

const CARD_SECTION = 'api-product-grid-item';
const RECENTLY_VIEWED_COOKIE = 'shopify_recently_viewed';
const SLIDE_CLASS = 'product-carousel-slide';

if (!customElements.get('product-carousel')) {
  customElements.define(
    'product-carousel',
    class ProductCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-carousel-track]');
        if (!this.track) return;

        this.limit = parseInt(this.dataset.limit, 10) || 4;
        this.aborter = new AbortController();

        /* Suggestions can arrive already populated — Liquid renders the cards
           when the section is requested through the recommendations endpoint.
           Nothing to do in that case. */
        if (this.track.children.length > 0) return;

        if (this.dataset.source === 'recently-viewed') {
          this.loadRecentlyViewed();
        } else if (this.dataset.source === 'recommendations') {
          this.loadRecommendations();
        }
      }

      disconnectedCallback() {
        if (this.aborter) this.aborter.abort();
      }

      /* = recently viewed = */

      async loadRecentlyViewed() {
        const handles = this.readCookie().slice(0, this.limit);
        if (!handles.length) return;

        const cards = await Promise.all(
          handles.map((handle, index) => this.fetchCard(handle, index))
        );
        /* Promise.all keeps order, so the row stays most-recent-first. Handles
           for deleted products resolve to null and drop out here. */
        this.render(cards.filter(Boolean));
      }

      readCookie() {
        const row = document.cookie
          .split('; ')
          .find((entry) => entry.startsWith(`${RECENTLY_VIEWED_COOKIE}=`));

        if (!row) return [];

        const handles = decodeURIComponent(row.split('=')[1] || '')
          .split(' ')
          .filter(Boolean);

        const current = this.currentHandle();
        return current ? handles.filter((handle) => handle !== current) : handles;
      }

      /* theme.js writes the current product into the cookie on every product
         page load, so it has to be filtered out again on read.

         Its own version of this (theme.js:807) looks for `#template-product`
         while the markup is `id="TemplateProduct"`. ID selectors are
         case-sensitive, so that lookup never matches and the product you are
         looking at shows up in its own "Recently viewed" row. Matching the real
         id here fixes that for this section without touching theme.js. The URL
         is a fallback for templates that do not render the element. */
      currentHandle() {
        const element = document.querySelector('#TemplateProduct');
        const fromElement = element && element.getAttribute('data-product-handle');
        if (fromElement) return fromElement;

        const path = decodeURIComponent(window.location.pathname);
        const marker = '/products/';
        const index = path.indexOf(marker);
        if (index === -1) return null;

        return path.slice(index + marker.length).split(/[/?#]/)[0] || null;
      }

      async fetchCard(handle, index) {
        const root = (window.theme && window.theme.routes && window.theme.routes.root) || '/';
        const url = `${root}products/${handle}?section_id=${CARD_SECTION}`;

        const markup = await this.fetchHTML(url);
        if (!markup) return null;

        const content = markup.querySelector('[data-api-content]');
        if (!content) return null;

        const slide = document.createElement('li');
        slide.className = SLIDE_CLASS;
        slide.innerHTML = this.fillPlaceholders(content.innerHTML, index);
        return slide;
      }

      fillPlaceholders(markup, index) {
        const anchor = this.id ? `#${this.id}` : '';

        return markup
          .replaceAll('||itemIndex||', index)
          .replaceAll('||itemAosDelay||', index * 150)
          .replaceAll('||itemAosImageDuration||', index * 100 + 800)
          .replaceAll('||itemAosTextDuration||', index * 50 + 800)
          .replaceAll('||itemAnimationAnchor||', anchor);
      }

      /* = recommendations = */

      async loadRecommendations() {
        const routes = (window.theme && window.theme.routes) || {};
        const productId = this.dataset.productId;

        if (!productId || !routes.product_recommendations_url) return;

        const url =
          `${routes.product_recommendations_url}?section_id=${this.dataset.sectionId}` +
          `&product_id=${productId}&limit=${this.limit}&intent=related`;

        const markup = await this.fetchHTML(url);
        if (!markup) return;

        /* Only the slides are moved across. Injecting the whole section would
           register a second <product-carousel>, which would fetch again, and
           again. */
        this.render(Array.from(markup.querySelectorAll(`.${SLIDE_CLASS}`)));
      }

      /* = shared = */

      async fetchHTML(url) {
        try {
          const response = await fetch(url, { signal: this.aborter.signal });
          if (!response.ok) return null;

          const text = await response.text();
          return new DOMParser().parseFromString(text, 'text/html');
        } catch (error) {
          /* Aborted on disconnect, or the request failed. Either way the
             section stays hidden rather than showing an empty row. */
          return null;
        }
      }

      render(slides) {
        if (!slides.length) return;

        slides.forEach((slide) => this.track.appendChild(slide));
        this.hidden = false;
      }
    }
  );
}
